import CreativeFileStatus from '@/Enums/Creatives/CreativeFileStatus.enum';
import CreativeType from '@/Enums/Creatives/CreativeType.enum';
import { IPaginatedApiResponse } from '@/Interfaces/ApiResponse/PaginatedApiResponse.interface';
import {
  ICreateDV360TicketsCreativePayload,
  IDV360TicketsCreative,
  IDV360TicketsCreativeFilters,
  IUpdateDV360TicketsCreativePayload,
} from '@/Interfaces/DV360Tickets/Creative.interface';
import { IRejectValueThunkConfig } from '@/Interfaces/Slices/AsyncThunk.interface';
import { DV360_TICKETS_CREATIVES_ENDPOINT, DV360_TICKETS_CREATIVES_PROCESS_ENDPOINT } from '@/constants/endpoints';
import {
  mapApiDV360TicketsCreativesToTableData,
  transformDV360TicketsCreativeFilters,
} from '@/helpers/dv360Tickets/creatives';
import { normalizeFileName } from '@/helpers/file';
import { addUnloadListener, removeUnloadListener } from '@/helpers/unloadListener';
import { setupVideoUploadWorker } from '@/helpers/worker';
import { RootState } from '@/store';
import { pendingCaseHandler, rejectedCaseHandler } from '@/store/asyncThunkHandlers';
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import axios from 'axios';
import { toast } from 'react-toastify';
import { getSignedUrl } from '../../api/storage.api';

enum actionPrefix {
  GET_CREATIVES = 'dv360TicketsCreativesSlice/getCreatives',
  CREATE_CREATIVE = 'dv360TicketsCreativesSlice/createCreative',
  UPDATE_CREATIVE = 'dv360TicketsCreativesSlice/updateCreative',
}

interface IState {
  filters: IDV360TicketsCreativeFilters;
  creatives: IDV360TicketsCreative[];
  total: number;
  creativesRequestId: string | null;
}

const initialState: IState = {
  filters: {
    types: [],
    page: 1,
    limit: 15,
    search: '',
    sort: null,
  },
  creatives: [],
  total: 0,
  creativesRequestId: null,
};

export const dv360TicketsCreativesSlice = createSlice({
  name: 'dv360creatives',
  initialState,
  reducers: {
    setFilters: (state, action) => {
      state.filters = {
        ...state.filters,
        ...action.payload,
      };
    },
  },
  extraReducers: builder => {
    builder
      .addCase(getDV360Creatives.pending, pendingCaseHandler('creatives'))
      .addCase(getDV360Creatives.rejected, rejectedCaseHandler('creatives'))
      .addCase(getDV360Creatives.fulfilled, (state, action) => {
        if (state.creativesRequestId === action.meta.requestId) {
          state.creatives = action.payload.data;
          state.total = action.payload.total;
          state.creativesRequestId = null;
        }
      });
  },
});

export const { setFilters } = dv360TicketsCreativesSlice.actions;

const handleDV360UploadSuccess = async (creativeId: number, filePath: string) => {
  toast.success('Video file uploaded successfully');
  try {
    await axios.post(DV360_TICKETS_CREATIVES_PROCESS_ENDPOINT(creativeId), { filePath });
  } catch {
    toast.error('Failed to process video file');
  }
};

const handleDV360UploadError = async (creativeId: number, error?: Error) => {
  toast.error('Failed to upload video file');
  console.error('Upload video file error', error);
  try {
    await axios.put(`${DV360_TICKETS_CREATIVES_ENDPOINT}/${creativeId}`, { fileStatus: CreativeFileStatus.FAILED });
  } catch {
    toast.error('Failed to update creative file status');
  }
};

const uploadDV360VideoFile = async (file: File, creativeId: number) => {
  const { signedUrl, filePath } = await getSignedUrl();
  const normalizedFile = normalizeFileName(file);

  addUnloadListener();

  let videoUploadWorker: Worker;

  const cleanup = () => {
    removeUnloadListener();
    videoUploadWorker?.terminate();
  };

  const onMessage = async (event: MessageEvent<{ success: boolean; error?: Error }>) => {
    if (event.data.success) {
      await handleDV360UploadSuccess(creativeId, filePath);
    } else {
      await handleDV360UploadError(creativeId, event.data.error);
    }
    cleanup();
  };

  videoUploadWorker = setupVideoUploadWorker(signedUrl, normalizedFile, onMessage);

  toast.info(
    'Uploading video file in background. Please do not refresh or close the page until the upload is complete.',
  );
};

export const getDV360Creatives = createAsyncThunk<{ data: IDV360TicketsCreative[]; total: number }>(
  actionPrefix.GET_CREATIVES,
  async (_, { getState }) => {
    const {
      dv360TicketsCreatives: { filters },
    } = getState() as RootState;

    const transformedFilters = transformDV360TicketsCreativeFilters(filters);
    const {
      data: {
        data,
        meta: { total },
      },
    } = await axios.get<IPaginatedApiResponse<IDV360TicketsCreative[]>>(DV360_TICKETS_CREATIVES_ENDPOINT, {
      params: transformedFilters,
    });

    return { data: mapApiDV360TicketsCreativesToTableData(data), total };
  },
);

export const createDV360Creative = createAsyncThunk<
  IDV360TicketsCreative,
  ICreateDV360TicketsCreativePayload,
  IRejectValueThunkConfig
>(actionPrefix.CREATE_CREATIVE, async (payload, { rejectWithValue }) => {
  try {
    const isVideoType = payload.type === CreativeType.VIDEO;
    const { file, ...restPayload } = payload;

    const { data } = await axios.post<IDV360TicketsCreative>(
      DV360_TICKETS_CREATIVES_ENDPOINT,
      isVideoType ? restPayload : payload,
    );

    if (isVideoType && file) {
      await uploadDV360VideoFile(file, data.id);
    }

    return data;
  } catch (err) {
    if (err instanceof Error) {
      return rejectWithValue(err);
    }
    return rejectWithValue(new Error('An unknown error occurred'));
  }
});

export const updateDV360Creative = createAsyncThunk<
  IDV360TicketsCreative,
  IUpdateDV360TicketsCreativePayload,
  IRejectValueThunkConfig
>(actionPrefix.UPDATE_CREATIVE, async (payload, { rejectWithValue }) => {
  try {
    const { id, type, ...restPayload } = payload;
    const data = await axios.put(`${DV360_TICKETS_CREATIVES_ENDPOINT}/${id}`, restPayload);
    return data.data;
  } catch (err) {
    if (err instanceof Error) {
      return rejectWithValue(err);
    }
    return rejectWithValue(new Error('An unknown error occurred'));
  }
});

export default dv360TicketsCreativesSlice.reducer;
